import {
  CopyOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignTopOutlined,
} from "@ant-design/icons";
import { message, Tooltip } from "antd";

import {
  displayOrDash,
  formatMoneyTypeLabel,
  formatReportAmount,
  formatReportDateTime,
  formatReportEmail,
  getPaymentStatusModifierKey,
  isDepositPaymentType,
  mapPaymentStatusLabel,
  mapPaymentTypeLabel,
} from "./reportGeneratorColumnUtils";
import { AppStrings } from "../../../utils/constants/AppStrings";
import styles from "./stylesTransactionsTable.module.css";

const STATUS_MODIFIER_CLASS = {
  approved: styles.statusApproved,
  "needs-review": styles.statusNeedsReview,
  rejected: styles.statusRejected,
  pending: styles.statusPending,
  "requires-action": styles.statusRequiresAction,
};

const getStatusClassName = (status) => {
  const modifier = STATUS_MODIFIER_CLASS[getPaymentStatusModifierKey(status)];
  return modifier ? `${styles.status} ${modifier}` : styles.status;
};

function CopyableIdCell({ value, copyAriaLabel }) {
  if (value == null || value === "") return <span>—</span>;
  return (
    <Tooltip title={String(value)}>
      <button
        type="button"
        className={styles.copyButton}
        aria-label={copyAriaLabel}
        onClick={(event) => {
          event.stopPropagation();
          navigator.clipboard
            .writeText(String(value))
            .then(() => message.success(AppStrings.PAM_COPIED_TO_CLIPBOARD))
            .catch(() =>
              message.error(
                AppStrings.NEW_AFFILIATE_CONFIRMATION_COPY_FAILED,
              ),
            );
        }}
      >
        <CopyOutlined />
      </button>
    </Tooltip>
  );
}

const sortOrderFor = (sort, field) => {
  if (sort === `+${field}`) return "ascend";
  if (sort === `-${field}`) return "descend";
  return null;
};

export function buildReportGeneratorColumns({ onOpenUser, sort = "-date" }) {
  return [
    {
      title: AppStrings.TRANSACTION_ID,
      dataIndex: "id",
      key: "id",
      width: 70,
      align: "center",
      render: (id) => (
        <CopyableIdCell
          value={id}
          copyAriaLabel={AppStrings.PAM_REPORT_COPY_ID}
        />
      ),
    },
    {
      title: AppStrings.DATE_TIME,
      dataIndex: "date",
      key: "date",
      width: 75,
      sorter: true,
      sortDirections: ["ascend", "descend", "ascend"],
      sortOrder: sortOrderFor(sort, "date"),
      render: (date) => formatReportDateTime(date),
    },
    {
      title: AppStrings.USER_ID,
      dataIndex: "player",
      key: "player",
      width: 55,
      align: "center",
      render: (player) => (
        <CopyableIdCell
          value={player}
          copyAriaLabel={AppStrings.PAM_REPORT_COPY_USER_ID}
        />
      ),
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "username",
      key: "username",
      width: 60,
      render: (username, record) => {
        if (!username) return "—";
        return (
          <span
            className={styles.linkValue}
            role="button"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              onOpenUser?.(record);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onOpenUser?.(record);
              }
            }}
          >
            {username}
          </span>
        );
      },
    },
    {
      title: AppStrings.EMAIL,
      dataIndex: "email",
      key: "email",
      width: 70,
      render: formatReportEmail,
    },
    {
      title: AppStrings.PROMO_CODE,
      dataIndex: "referral_code",
      key: "referral_code",
      width: 55,
      render: displayOrDash,
    },
    {
      title: AppStrings.AMOUNT,
      dataIndex: "amount",
      key: "amount",
      width: 55,
      sorter: true,
      sortOrder: sortOrderFor(sort, "amount"),
      render: (amount) => {
        const numeric = Number(amount);
        const className =
          Number.isFinite(numeric) && numeric < 0
            ? styles.negativeAmount
            : styles.positiveAmount;
        return <span className={className}>{formatReportAmount(amount)}</span>;
      },
    },
    {
      title: AppStrings.MONEY_TYPE,
      dataIndex: "money_type",
      key: "money_type",
      width: 60,
      render: formatMoneyTypeLabel,
    },
    {
      title: AppStrings.TYPE,
      dataIndex: "type",
      key: "type",
      width: 55,
      render: (type) => {
        const label = mapPaymentTypeLabel(type);
        if (label === "—") return label;
        return (
          <div className={styles.transactionType}>
            {isDepositPaymentType(type) ? (
              <VerticalAlignBottomOutlined />
            ) : (
              <VerticalAlignTopOutlined />
            )}
            <span>{label}</span>
          </div>
        );
      },
    },
    {
      title: AppStrings.PLATFORM,
      dataIndex: "platform",
      key: "platform",
      width: 50,
      render: displayOrDash,
    },
    {
      title: AppStrings.PAYMENT_PROVIDER,
      dataIndex: "provider",
      key: "provider",
      width: 55,
      render: displayOrDash,
    },
    {
      title: AppStrings.STATUS,
      dataIndex: "status",
      key: "status",
      width: 70,
      render: (status) => (
        <span className={getStatusClassName(status)}>
          {mapPaymentStatusLabel(status)}
        </span>
      ),
    },
    {
      title: AppStrings.STATE,
      dataIndex: "state",
      key: "state",
      width: 50,
      render: displayOrDash,
    },
    {
      title: AppStrings.PAYMENT_RAW_STATUS,
      dataIndex: "raw_status",
      key: "raw_status",
      width: 60,
      render: displayOrDash,
    },
  ];
}
